"use client";
// PC 首页「全球加速网络 + 数据背书」区：世界地图节点连线 + 真实数据统计（游戏数/分类数由服务端读库传入，数字滚动动画）
import React from "react";
import { WorldMap } from "./world-map";
import { motion } from "motion/react";
import CountUp from "./CountUp";
import { homeDict, type HomeAccelDict } from "@/lib/i18n/dicts/home";

// 香港核心枢纽经纬度（显示名由 dict.accel.hub 注入）
const HUB = { lat: 22.3193, lng: 114.1694 };

// 全球加速节点经纬度（显示名由 dict.accel.cities 按顺序注入）
const ACCEL_NODES = [
  { lat: 34.0522, lng: -118.2437 }, // 洛杉矶
  { lat: 50.1109, lng: 8.6821 }, // 法兰克福
  { lat: 1.3521, lng: 103.8198 }, // 新加坡
  { lat: 35.6762, lng: 139.6503 }, // 东京
  { lat: -23.5505, lng: -46.6333 }, // 圣保罗
  { lat: -33.8688, lng: 151.2093 }, // 悉尼
  { lat: 25.2048, lng: 55.2708 }, // 迪拜
  { lat: 19.076, lng: 72.8777 }, // 孟买
  { lat: 51.5074, lng: -0.1278 }, // 伦敦
  { lat: 37.5665, lng: 126.978 }, // 首尔
];

// 数据背书：游戏数/分类数由 page.tsx 读库传入
export interface AccelCounts {
  games: number;
  categories: number;
}

export interface GlobalAccelerationSectionProps {
  dict?: HomeAccelDict;
  counts: AccelCounts;
}

export default function GlobalAccelerationSection({ dict = homeDict.zh.accel, counts }: GlobalAccelerationSectionProps) {
  const dots = ACCEL_NODES.map((node, i) => ({
    start: { ...HUB, label: dict.hub },
    end: { ...node, label: dict.cities[i] },
  }));

  // 4 项指标：前两项数值读库，后两项数值写字典
  const stats = [
    { value: counts.games, suffix: dict.stats.games.suffix, label: dict.stats.games.label },
    { value: counts.categories, suffix: dict.stats.categories.suffix, label: dict.stats.categories.label },
    { value: dict.stats.languages.value, suffix: dict.stats.languages.suffix, label: dict.stats.languages.label },
    { value: dict.stats.nodes.value, suffix: dict.stats.nodes.suffix, label: dict.stats.nodes.label },
  ];

  return (
    <section className="w-full bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-sm font-semibold tracking-widest text-indigo-600 uppercase">
            {dict.kicker}
          </p>
          <h2 className="mt-2 text-2xl md:text-4xl font-bold text-gray-900">
            {dict.heading}
          </h2>
          <p className="mt-4 text-sm md:text-lg text-neutral-500 max-w-2xl mx-auto">
            {dict.sub}
          </p>
        </motion.div>

        {/* 数据统计（数字滚动动画） */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              className="text-center relative"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              {/* 数字背后的呼吸柔光 */}
              <span aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-16 w-16 rounded-full bg-indigo-400/30 blur-xl animate-breathe-glow"
                style={{ animationDelay: `${i * 0.7}s` }} />
              <CountUp
                value={s.value}
                suffix={s.suffix}
                className="text-2xl md:text-4xl font-bold text-indigo-700"
              />
              <p className="mt-1 text-xs md:text-sm text-gray-500">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {/* 世界地图 + 节点连接动画 */}
        <motion.div
          className="mt-8"
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <WorldMap lineColor="#4338ca" dots={dots} loop idleSeconds={4} />
        </motion.div>

        <motion.p
          className="mt-4 text-center text-xs text-gray-400"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {dict.footnote}
        </motion.p>
      </div>
    </section>
  );
}
